import type {
  ServerWrappedResponse,
  ServerWrappedTotalsResponse,
  ServerCityVisited,
  ServerTopDestination,
  ServerTopOffer,
  ServerLoyalty,
  ServerPrediction,
  ServerActiveBooking,
} from './types'
import type {
  WrappedData,
  WrappedTotalsData,
  CityVisited,
  TopDestination,
  TopOffer,
  LoyaltyData,
  PredictionData,
  ActiveBooking,
  TravelPersonalityType,
} from 'components/Pages/Wrapped/types/wrapped'
import { getInsuranceCountryTwoLetterCodeByName } from '@luxuryescapes/lib-regions'
import uuidV4 from 'lib/string/uuidV4Utils'

function mapCityVisited(city: ServerCityVisited): CityVisited {
  const countryName = city.countryCode
  const isoCode = getInsuranceCountryTwoLetterCodeByName(countryName) ?? ''

  return {
    name: city.name,
    country: countryName,
    countryCode: isoCode,
  }
}

function mapTopDestination(dest: ServerTopDestination): TopDestination {
  return {
    id: dest.id ?? dest.name ?? '',
    name: dest.name,
    viewCount: dest.viewCount,
    imageId: dest.imageId ?? '',
  }
}

function mapTopOffer(offer: ServerTopOffer | null): TopOffer | undefined {
  if (!offer?.id || !offer.name) {
    return undefined
  }

  const inclusionsList = offer.inclusions?.tileInclusions ?? []
  const mappedInclusions = inclusionsList.map(inclusion => ({
    id: uuidV4(),
    description: inclusion.text,
  }))

  const locationString = offer.location
    ? `${offer.location.heading}, ${offer.location.subheading}`
    : ''

  return {
    id: offer.id,
    isAlternative: !!offer.alternativeOfferId,
    name: offer.name,
    propertyName: offer.propertyName ?? offer.location?.description ?? '',
    location: locationString,
    viewCount: offer.viewCount ?? 0,
    imageId: offer.imageId ?? '',
    inclusions: mappedInclusions,
  }
}

const TIER_ABBREVIATION_MAP: Record<string, LoyaltyData['tier']> = {
  br: 'bronze',
  si: 'silver',
  go: 'gold',
  pl: 'platinum',
  bronze: 'bronze',
  silver: 'silver',
  gold: 'gold',
  platinum: 'platinum',
}

const NEXT_TIER_MAP: Record<LoyaltyData['tier'], LoyaltyData['tier'] | null> = {
  bronze: 'silver',
  silver: 'gold',
  gold: 'platinum',
  platinum: null,
}

const TIER_DISPLAY_NAMES: Record<LoyaltyData['tier'], string> = {
  bronze: 'Bronze',
  silver: 'Silver',
  gold: 'Gold',
  platinum: 'Platinum',
}

function mapLoyalty(loyalty: ServerLoyalty): LoyaltyData | undefined {
  if (!loyalty.tier || loyalty.points === null) {
    return undefined
  }

  const tierKey = loyalty.tier.toLowerCase()
  const tier = TIER_ABBREVIATION_MAP[tierKey]
  if (!tier) {
    return undefined
  }

  const result: LoyaltyData = {
    tier,
    points: loyalty.points,
    statusCredits: loyalty.statusCredits ?? 0,
    percentile: loyalty.percentile ?? 0,
  }

  if (loyalty.nextTier.creditsNeeded !== null || loyalty.nextTier.nightsNeeded !== null) {
    const nextTier = NEXT_TIER_MAP[tier]
    result.tierProgression = {
      type: 'unlock',
      name: nextTier ? TIER_DISPLAY_NAMES[nextTier] : undefined,
      creditsNeeded: loyalty.nextTier.creditsNeeded ?? 0,
      nightsNeeded: loyalty.nextTier.nightsNeeded ?? 0,
      benefits: [],
    }
  }

  return result
}

function extractDestinationIdFromUrl(url: string | null): string | null {
  if (!url) return null
  try {
    const queryString = url.split('?')[1] ?? ''
    const params = new URLSearchParams(queryString)
    return params.get('destinationId')
  } catch {
    return null
  }
}

function mapPrediction(prediction: ServerPrediction): PredictionData | undefined {
  if (!prediction.destination || !prediction.imageId) {
    return undefined
  }

  // Extract destinationId from searchUrl - the backend provides the proper place ID there
  const destinationId = extractDestinationIdFromUrl(prediction.searchUrl)

  return {
    destinationId: destinationId ?? '',
    destinationName: prediction.destination,
    offerCount: prediction.offerCount ?? 0,
    maxSavingsPercent: prediction.maxSavingsPercent ?? 0,
    imageId: prediction.imageId,
    searchUrl: prediction.searchUrl ?? `/search?destination=${encodeURIComponent(prediction.destination.toLowerCase())}`,
  }
}

function mapActiveBooking(booking: ServerActiveBooking | null): ActiveBooking | undefined {
  if (!booking?.destination || !booking.checkInDate || !booking.orderId) {
    return undefined
  }

  const checkIn = new Date(booking.checkInDate)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  checkIn.setHours(0, 0, 0, 0)

  const diffTime = checkIn.getTime() - today.getTime()
  const daysToGo = Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)))

  return {
    orderId: booking.orderId,
    daysToGo,
    destination: booking.destination,
    checkInDate: booking.checkInDate,
    imageId: booking.imageId ?? '',
  }
}

function mapWrappedResponse(response: ServerWrappedResponse): WrappedData {
  const personalityType = response.personality.type as TravelPersonalityType
  const topOffer = mapTopOffer(response.topOffer)

  const mappedData: WrappedData = {
    personality: {
      type: personalityType,
      percentile: response.personality.percentile,
    },
    stats: {
      totalNights: response.stats.totalNights ?? 0,
      totalDistanceKm: response.stats.totalDistanceKm ?? 0,
      totalSavings: response.stats.totalSavings ?? 0,
      currency: response.stats.currency ?? 'AUD',
      citiesVisited: (response.stats.citiesVisited ?? []).map(mapCityVisited),
      countriesVisited: response.stats.countriesVisited ?? 0,
      offersViewed: response.stats.offersViewed ?? 0,
      averageOffersViewed: response.stats.averageOffersViewed ?? 0,
      destinationsSearched: response.stats.destinationsViewed ?? 0,
    },

    topDestinations: (response.topDestinations ?? []).map(mapTopDestination),

    topOffer: topOffer ?? {
      id: '',
      name: '',
      propertyName: '',
      location: '',
      viewCount: 0,
      imageId: '',
      isAlternative: false,
      inclusions: [],
    },

    generatedAt: new Date().toISOString(),
  }

  const loyalty = mapLoyalty(response.loyalty)
  if (loyalty) {
    mappedData.loyalty = loyalty
  }

  const prediction = mapPrediction(response.prediction)
  if (prediction) {
    mappedData.prediction = prediction
  }

  const activeBooking = mapActiveBooking(response.activeBooking)
  if (activeBooking) {
    mappedData.activeBooking = activeBooking
  }

  return mappedData
}

export function mapWrappedTotalsResponse(response: ServerWrappedTotalsResponse): WrappedTotalsData {
  const topOffer = mapTopOffer(response.topOffer)

  return {
    stats: {
      totalNights: response.stats.totalNights ?? 1578458,
      totalDistanceKm: response.stats.totalDistanceKm ?? 2211849128,
      totalSavings: response.stats.totalSavings ?? 470069636,
      currency: response.stats.currency ?? 'AUD',
      offersViewed: response.stats.offersViewed ?? 103195393,
      averageOffersViewed: response.stats.averageOffersViewed ?? 0,
      destinationsSearched: response.stats.destinationsViewed ?? 22508,
    },

    topDestinations: (response.topDestinations ?? []).map(mapTopDestination),

    topOffer: topOffer ?? {
      id: '',
      name: '',
      propertyName: '',
      location: '',
      viewCount: 0,
      imageId: '',
      isAlternative: false,
      inclusions: [],
    },

    generatedAt: new Date().toISOString(),
  }
}

export default mapWrappedResponse
